import dayjs from "dayjs";
import { trim } from "lodash";
import { useTranslation } from "react-i18next";

export const useTime = () => {
    const { t } = useTranslation("time");

    const weekdays: string[] = [
        t("monday"),
        t("tuesday"),
        t("wednesday"),
        t("thursday"),
        t("friday"),
        t("saturday"),
        t("sunday"),
    ];
    const weekdaysShort: string[] = [
        t("mon"),
        t("tue"),
        t("wed"),
        t("thu"),
        t("fri"),
        t("sat"),
        t("sun"),
    ];

    const months: string[] = [
        t("january"),
        t("february"),
        t("march"),
        t("april"),
        t("may"),
        t("june"),
        t("july"),
        t("august"),
        t("september"),
        t("october"),
        t("november"),
        t("december"),
    ];

    const monthsShort: string[] = [
        t("jan"),
        t("feb"),
        t("mar"),
        t("apr"),
        t("may"),
        t("jun"),
        t("jul"),
        t("aug"),
        t("sep"),
        t("oct"),
        t("nov"),
        t("dec"),
    ];

    const getWeekdayByDate = ({
        date,
        short = false,
    }: {
        date: Date | string;
        short?: boolean;
    }) => {
        if (!date || !dayjs(date).isValid()) {
            return;
        }

        // 0 (Sunday) to 6 (Saturday) in dayjs
        const index = (dayjs(date).day() + 6) % 7;
        return short ? weekdaysShort[index] : weekdays[index];
    };

    const getMonthByDate = ({
        date,
        short = false,
    }: {
        date: Date | string;
        short?: boolean;
    }) => {
        if (!date || !dayjs(date).isValid()) {
            return;
        }
        const index = dayjs(date).month();
        return short ? monthsShort[index] : months[index];
    };

    const getTimeInSeconds = ({
        fromDate,
        toDate = new Date(),
    }: {
        fromDate: Date;
        toDate?: Date;
        conversionFactor?: number;
    }) => {
        return Math.floor((toDate.getTime() - fromDate.getTime()) / 1000);
    };

    const getFormattedTimeString = (
        seconds: number,
        isDetailedTime: boolean = false
    ) => {
        if (isDetailedTime) {
            const days = Math.trunc(seconds / (60 * 60 * 24));
            const dayRest = seconds - days * (60 * 60 * 24);
            const hours = Math.trunc(dayRest / (60 * 60));
            const hoursRest = dayRest - hours * 60 * 60;
            const minutes = Math.trunc(hoursRest / 60);

            let daysString = "";
            let hoursString = "";
            var minutesString = "";
            if (days > 0) {
                daysString = `${days} ${t("time:day", {
                    count: days,
                })}`;
            }
            if (hours > 0) {
                hoursString = `${hours} ${t("time:hour_short", {
                    count: hours,
                })}`;
            }
            if (minutes > 0) {
                minutesString = `${minutes} ${t("time:minute_short", {
                    count: minutes,
                })}`;
            }

            if (minutes == 0 && hours == 0 && days == 0) {
                return `< 1 ${t("time:minute_short", {
                    count: minutes,
                })}`;
            }

            return trim(`${daysString} ${hoursString} ${minutesString}`);
        }

        if (seconds < 60) {
            return `${seconds} ${t("time:second", {
                count: seconds,
            })}`;
        }

        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) {
            return `${minutes} ${t("time:minute", {
                count: minutes,
            })}`;
        }

        const hours = Math.floor(minutes / 60);
        if (hours < 24) {
            return `${hours} ${t("time:hour", { count: hours })}`;
        }

        const days = Math.floor(hours / 24);
        return `${days} ${t("time:day", { count: days })}`;
    };

    const getRoundedTimeString = (seconds: number) => {
        if (seconds < 60) {
            return `< 1 ${t("time:minute_short")}`;
        }

        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) {
            return `${minutes} ${t("time:minute_short")}`;
        }

        var hours = Math.floor(minutes / 60);
        if (hours < 24) {
            if (minutes % 60 > 30) {
                hours = hours + 1;
            }
            return `${hours} ${t("time:hour_short")}`;
        }
        const days = Math.floor(hours / 24);
        return `${days} ${t("time:day", { count: days })}`;
    };

    return {
        getWeekdayByDate,
        getMonthByDate,
        getTimeInSeconds,
        getFormattedTimeString,
        getRoundedTimeString,
        weekdays,
        weekdaysShort,
        months,
        monthsShort,
    };
};
